// src/pages/saneamentoFiscal.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "../components/mainLayout";
import { api } from "../api";
import { showError, showInfo } from "../utils/toast";
import {
  FiActivity,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiDatabase,
  FiEdit3,
  FiFileText,
  FiList,
  FiPackage,
  FiPlay,
  FiRefreshCw,
  FiSave,
  FiSearch,
  FiShield,
  FiStopCircle,
  FiUser,
  FiX,
  FiZap,
} from "react-icons/fi";
import "../sass/pages/saneamentoFiscal.scss";

const PROCESSAMENTO_TIMEOUT_MS = 180000;
const MONITORAMENTO_PROCESSAMENTO_MS = 5000;

const STATUS_PRODUTOS = [
  { value: "", label: "Todos" },
  { value: "PENDENTE", label: "Pendente" },
  { value: "AGUARDANDO_CONSULTA", label: "Aguardando consulta" },
  { value: "CONSULTANDO_IMENDES", label: "Consultando IMendes" },
  { value: "SANEADO_IMENDES", label: "Saneado IMendes" },
  { value: "SANEADO_MANUALMENTE", label: "Saneado manual" },
  { value: "PENDENTE_IMENDES", label: "Pendente IMendes" },
  { value: "ERRO_IMENDES", label: "Erro IMendes" },
  { value: "ERRO_PROCESSAMENTO", label: "Erro processamento" },
  { value: "OPERACIONALMENTE_VALIDADO", label: "Validado operacional" },
];

const STATUS_PENDENCIA = [
  { value: "", label: "Pendências e erros" },
  { value: "PENDENTE_IMENDES", label: "Pendente IMendes" },
  { value: "ERRO_IMENDES", label: "Erro IMendes" },
  { value: "ERRO_PROCESSAMENTO", label: "Erro processamento" },
];

const FORM_MANUAL_INICIAL = {
  CD_NCM: "",
  CFOP: "5102",
  CSOSN_CST: "",
  PERC_ICMS: "",
  CST_PIS: "01",
  PERC_PIS: "1,65",
  CST_COFINS: "01",
  PERC_COFINS: "7,60",
  CEST: "",
  CD_ANP: "",
  CD_CBENEF: "",
  CST_IBS_CBS: "",
  CLASS_TRIB: "",
  PERC_IBS_UF: "",
  PERC_IBS_MUN: "",
  PERC_CBS: "",
  DS_OBSERVACAO: "Saneado manualmente pela Central Interna de Saneamento Fiscal.",
};

function toStr(value) {
  return value === null || value === undefined ? "" : String(value).trim();
}

function toNumberOrNull(value) {
  const text = toStr(value).replace(",", ".");
  if (!text) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

function fmtInt(value) {
  return Number(value || 0).toLocaleString("pt-BR");
}

function fmtPercent(value) {
  const n = Number(value || 0);
  return `${n.toLocaleString("pt-BR", {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}%`;
}

function fmtDateOnly(value) {
  if (!value) return "-";

  const text = String(value).slice(0, 10);
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;

  return text;
}

function fmtDate(value) {
  if (!value) return "-";

  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

function safeText(value, fallback = "-") {
  const text = toStr(value);
  return text || fallback;
}

function obterUsuarioSessao() {
  try {
    const raw = sessionStorage.getItem("user") || localStorage.getItem("user");
    const user = raw ? JSON.parse(raw) : {};

    const nome =
      sessionStorage.getItem("RazaoSocial") ||
      sessionStorage.getItem("Nome") ||
      localStorage.getItem("RazaoSocial") ||
      localStorage.getItem("Nome") ||
      user?.nome ||
      user?.Nome ||
      user?.usuario ||
      "Usuário interno";

    const id =
      sessionStorage.getItem("IdFuncionario") ||
      sessionStorage.getItem("Idfuncionario") ||
      sessionStorage.getItem("codFunc") ||
      localStorage.getItem("IdFuncionario") ||
      localStorage.getItem("Idfuncionario") ||
      localStorage.getItem("codFunc") ||
      user?.IdFuncionario ||
      user?.idFuncionario ||
      user?.codFunc ||
      user?.id ||
      0;

    return {
      id: Number(id || 0),
      nome: String(nome || "Usuário interno").trim(),
    };
  } catch {
    return { id: 0, nome: "Usuário interno" };
  }
}

function iniciais(nome) {
  const partes = String(nome || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!partes.length) return "SI";
  return partes
    .slice(0, 2)
    .map((parte) => parte.charAt(0).toUpperCase())
    .join("");
}

async function apiGet(path, params = {}) {
  const { data } = await api.get(path, { params });
  return data;
}

async function apiPost(path, body = {}, config = {}) {
  const { data } = await api.post(path, body, config);
  return data;
}

function obterMensagemErro(error, fallback = "Falha na operação.") {
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.msg ||
    error?.message ||
    fallback;

  return String(message);
}

function isTimeoutError(error) {
  const message = String(error?.message || "").toLowerCase();
  return (
    error?.code === "ECONNABORTED" ||
    message.includes("timeout") ||
    message.includes("exceeded")
  );
}

function fmtTempoDecorrido(inicio) {
  if (!inicio) return "0s";

  const inicial = new Date(inicio).getTime();
  if (!Number.isFinite(inicial)) return "0s";

  const segundos = Math.max(0, Math.floor((Date.now() - inicial) / 1000));
  const min = Math.floor(segundos / 60);
  const sec = segundos % 60;

  if (min <= 0) return `${sec}s`;
  return `${min}m ${String(sec).padStart(2, "0")}s`;
}

function unwrapResult(json, fallback = {}) {
  if (!json) return fallback;
  if (json.result !== undefined) return json.result;
  return json;
}

function getStatusClass(status) {
  const value = toStr(status).toUpperCase();

  if (["SANEADO_IMENDES", "SANEADO_MANUALMENTE", "OPERACIONALMENTE_VALIDADO", "FINALIZADA"].includes(value)) {
    return "sf-status--ok";
  }

  if (["PENDENTE", "AGUARDANDO_CONSULTA", "PENDENTE_IMENDES"].includes(value)) {
    return "sf-status--warn";
  }

  if (["ERRO_IMENDES", "ERRO_PROCESSAMENTO", "ERRO", "FALHA"].includes(value)) {
    return "sf-status--bad";
  }

  if (["CONSULTANDO_IMENDES", "EM_EXECUCAO", "PROCESSANDO"].includes(value)) {
    return "sf-status--info";
  }

  return "sf-status--neutral";
}

function StatusBadge({ status }) {
  return <span className={`sf-status ${getStatusClass(status)}`}>{safeText(status, "SEM STATUS")}</span>;
}


function isLicencaComEsteiraAtiva(licenca) {
  const modo =
    licenca?.MODO_AUTOMATICO ??
    licenca?.modoAutomatico ??
    licenca?.automatico ??
    licenca?.ativoAutomatico ??
    "N";

  return ["S", "SIM", "TRUE", "1", true].includes(
    typeof modo === "boolean" ? modo : String(modo).trim().toUpperCase()
  );
}

function getNomeLicenca(licenca) {
  return (
    licenca?.NM_FANTASIA ||
    licenca?.nomeFantasia ||
    licenca?.NOME_FANTASIA ||
    licenca?.RAZAO_SOCIAL ||
    licenca?.NM_RAZAO_SOCIAL ||
    licenca?.razaoSocial ||
    licenca?.DS_CLIENTE ||
    licenca?.cliente ||
    ""
  );
}

function getDocumentoLicenca(licenca) {
  return (
    licenca?.NR_CNPJ ||
    licenca?.CNPJ ||
    licenca?.cnpj ||
    licenca?.CPF_CNPJ ||
    licenca?.CpfCnpj ||
    ""
  );
}

function textoBuscaLicenca(licenca) {
  return [
    licenca?.NR_LICENCA,
    licenca?.ID_EMPRESA_PADRAO,
    getNomeLicenca(licenca),
    getDocumentoLicenca(licenca),
    licenca?.OBSERVACAO,
  ]
    .map((v) => toStr(v).toLowerCase())
    .filter(Boolean)
    .join(" ");
}

function KpiCard({ label, value, hint, tone = "info", icon: Icon }) {
  return (
    <div className={`sf-kpi sf-kpi--${tone}`}>
      <div className="sf-kpi-icon">{Icon ? <Icon /> : <FiShield />}</div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        {hint ? <small>{hint}</small> : null}
      </div>
    </div>
  );
}

function InfoItem({ label, value, strong = false }) {
  return (
    <div className="sf-info-item">
      <span>{label}</span>
      {strong ? <strong>{safeText(value)}</strong> : <p>{safeText(value)}</p>}
    </div>
  );
}

function montarFormManual(row) {
  return {
    ...FORM_MANUAL_INICIAL,
    CD_NCM: safeText(row?.CD_NCM_ORIGINAL || row?.CD_NCM_IMENDES, ""),
    CFOP: safeText(row?.CFOP_ORIGINAL || row?.CFOP_IMENDES || "5102", "5102"),
    CSOSN_CST: safeText(
      row?.CSOSN_ORIGINAL ||
        row?.CST_ICMS_ORIGINAL ||
        row?.CSOSN_IMENDES ||
        row?.CST_ICMS_IMENDES,
      ""
    ),
    PERC_ICMS: safeText(row?.P_ICMS_ORIGINAL || row?.P_ICMS_IMENDES, ""),
    CST_PIS: safeText(row?.CST_PIS_ORIGINAL || row?.CST_PIS_IMENDES || "01", "01"),
    PERC_PIS: safeText(row?.P_PIS_ORIGINAL || row?.P_PIS_IMENDES || "1,65", "1,65"),
    CST_COFINS: safeText(row?.CST_COFINS_ORIGINAL || row?.CST_COFINS_IMENDES || "01", "01"),
    PERC_COFINS: safeText(row?.P_COFINS_ORIGINAL || row?.P_COFINS_IMENDES || "7,60", "7,60"),
    CEST: safeText(row?.CD_CEST_ORIGINAL || row?.CD_CEST_IMENDES, ""),
    CD_ANP: safeText(row?.C_ANP_ORIGINAL || row?.C_ANP_IMENDES, ""),
    CD_CBENEF: safeText(row?.CBENEF_ORIGINAL || row?.CBENEF_IMENDES, ""),
  };
}

function normalizarPayloadManual(form) {
  const limpar = (value) => {
    const text = toStr(value);
    return text ? text : null;
  };

  return {
    CD_NCM: limpar(form.CD_NCM),
    CFOP: limpar(form.CFOP),
    CSOSN_CST: limpar(form.CSOSN_CST),
    PERC_ICMS: toNumberOrNull(form.PERC_ICMS),
    CST_PIS: limpar(form.CST_PIS),
    PERC_PIS: toNumberOrNull(form.PERC_PIS),
    CST_COFINS: limpar(form.CST_COFINS),
    PERC_COFINS: toNumberOrNull(form.PERC_COFINS),
    CEST: limpar(form.CEST),
    CD_ANP: limpar(form.CD_ANP),
    CD_CBENEF: limpar(form.CD_CBENEF),
    CST_IBS_CBS: limpar(form.CST_IBS_CBS),
    CLASS_TRIB: limpar(form.CLASS_TRIB),
    PERC_IBS_UF: toNumberOrNull(form.PERC_IBS_UF),
    PERC_IBS_MUN: toNumberOrNull(form.PERC_IBS_MUN),
    PERC_CBS: toNumberOrNull(form.PERC_CBS),
    DS_OBSERVACAO: limpar(form.DS_OBSERVACAO),
  };
}

function ManualModal({ pendencia, form, setForm, salvando, onClose, onSalvar }) {
  if (!pendencia) return null;

  function alterar(campo, valor) {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  }

  return (
    <div className="sf-modal-backdrop" onMouseDown={onClose}>
      <div className="sf-modal" onMouseDown={(e) => e.stopPropagation()}>
        <header className="sf-modal-head">
          <div>
            <span className="sf-eyebrow">Saneamento manual</span>
            <h2>{safeText(pendencia.DS_PRODUTO_SERVICO, "Produto pendente")}</h2>
            <p>
              Licença {safeText(pendencia.NR_LICENCA)} • Produto #{safeText(pendencia.ID_PRODUTO_SERVICO)} • Histórico #{safeText(pendencia.ID_PRODUTO_REGRA_SANEADA)}
            </p>
          </div>

          <button type="button" className="sf-icon-btn" onClick={onClose} disabled={salvando}>
            <FiX />
          </button>
        </header>

        <div className="sf-modal-context">
          <InfoItem label="Código/EAN" value={pendencia.CD_EAN || pendencia.CD_PRODUTO_SERVICO} />
          <InfoItem label="NCM original" value={pendencia.CD_NCM_ORIGINAL} />
          <InfoItem label="Status" value={pendencia.DM_STATUS} />
          <InfoItem label="Motivo" value={pendencia.MOTIVO_STATUS} />
        </div>

        <div className="sf-form-grid">
          <label className="sf-field">
            <span>NCM</span>
            <input value={form.CD_NCM} onChange={(e) => alterar("CD_NCM", e.target.value)} maxLength={8} />
          </label>

          <label className="sf-field">
            <span>CFOP</span>
            <input value={form.CFOP} onChange={(e) => alterar("CFOP", e.target.value)} maxLength={10} />
          </label>

          <label className="sf-field">
            <span>CST/CSOSN</span>
            <input value={form.CSOSN_CST} onChange={(e) => alterar("CSOSN_CST", e.target.value)} maxLength={4} />
          </label>

          <label className="sf-field">
            <span>% ICMS</span>
            <input value={form.PERC_ICMS} onChange={(e) => alterar("PERC_ICMS", e.target.value)} />
          </label>

          <label className="sf-field">
            <span>CST PIS</span>
            <input value={form.CST_PIS} onChange={(e) => alterar("CST_PIS", e.target.value)} maxLength={2} />
          </label>

          <label className="sf-field">
            <span>% PIS</span>
            <input value={form.PERC_PIS} onChange={(e) => alterar("PERC_PIS", e.target.value)} />
          </label>

          <label className="sf-field">
            <span>CST COFINS</span>
            <input value={form.CST_COFINS} onChange={(e) => alterar("CST_COFINS", e.target.value)} maxLength={2} />
          </label>

          <label className="sf-field">
            <span>% COFINS</span>
            <input value={form.PERC_COFINS} onChange={(e) => alterar("PERC_COFINS", e.target.value)} />
          </label>

          <label className="sf-field">
            <span>CEST</span>
            <input value={form.CEST} onChange={(e) => alterar("CEST", e.target.value)} maxLength={7} />
          </label>

          <label className="sf-field">
            <span>cBenef</span>
            <input value={form.CD_CBENEF} onChange={(e) => alterar("CD_CBENEF", e.target.value)} maxLength={20} />
          </label>

          <label className="sf-field">
            <span>C-ANP</span>
            <input value={form.CD_ANP} onChange={(e) => alterar("CD_ANP", e.target.value)} maxLength={20} />
          </label>

          <label className="sf-field">
            <span>CST IBS/CBS</span>
            <input value={form.CST_IBS_CBS} onChange={(e) => alterar("CST_IBS_CBS", e.target.value)} maxLength={3} />
          </label>

          <label className="sf-field">
            <span>Class. Trib.</span>
            <input value={form.CLASS_TRIB} onChange={(e) => alterar("CLASS_TRIB", e.target.value)} maxLength={6} />
          </label>

          <label className="sf-field">
            <span>% IBS UF</span>
            <input value={form.PERC_IBS_UF} onChange={(e) => alterar("PERC_IBS_UF", e.target.value)} />
          </label>

          <label className="sf-field">
            <span>% IBS Mun.</span>
            <input value={form.PERC_IBS_MUN} onChange={(e) => alterar("PERC_IBS_MUN", e.target.value)} />
          </label>

          <label className="sf-field">
            <span>% CBS</span>
            <input value={form.PERC_CBS} onChange={(e) => alterar("PERC_CBS", e.target.value)} />
          </label>

          <label className="sf-field sf-field--full">
            <span>Observação</span>
            <textarea value={form.DS_OBSERVACAO} onChange={(e) => alterar("DS_OBSERVACAO", e.target.value)} rows={3} />
          </label>
        </div>

        <footer className="sf-modal-actions">
          <button type="button" className="sf-btn sf-btn--soft" onClick={onClose} disabled={salvando}>
            Cancelar
          </button>
          <button type="button" className="sf-btn sf-btn--primary" onClick={onSalvar} disabled={salvando}>
            <FiSave />
            {salvando ? "Aplicando..." : "Aplicar regra final"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function HistoricoProdutoModal({ historico, carregando, onClose }) {
  if (!historico && !carregando) return null;

  const produto = historico?.produtoAtual || null;
  const eventos = Array.isArray(historico?.eventos) ? historico.eventos : [];

  return (
    <div className="sf-modal-backdrop" onMouseDown={onClose}>
      <div
        className="sf-modal sf-history-modal"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="sf-modal-head">
          <div>
            <span className="sf-eyebrow">Rastreabilidade do produto</span>
            <h2>{safeText(produto?.DS_PRODUTO_SERVICO, carregando ? "Carregando histórico..." : "Histórico do produto")}</h2>
            <p>
              Licença {safeText(historico?.NR_LICENCA)} • Produto #{safeText(historico?.ID_PRODUTO_SERVICO)}
            </p>
          </div>

          <button type="button" className="sf-icon-btn" onClick={onClose}>
            <FiX />
          </button>
        </header>

        {carregando ? (
          <div className="sf-history-loading">
            <FiRefreshCw />
            <strong>Carregando linha do tempo...</strong>
          </div>
        ) : (
          <>
            <div className="sf-modal-context">
              <InfoItem label="Status atual" value={produto?.DM_STATUS} />
              <InfoItem label="NCM original" value={produto?.CD_NCM_ORIGINAL} />
              <InfoItem label="Históricos" value={fmtInt(historico?.totalHistoricos)} strong />
              <InfoItem label="Processamentos" value={fmtInt(historico?.totalProcessamentos)} strong />
            </div>

            <div className="sf-history-body">
              {eventos.length ? (
                <div className="sf-timeline">
                  {eventos.map((evento, index) => {
                    const dados = evento?.dados || {};
                    const processamento = evento?.tipo === "PROCESSAMENTO";

                    return (
                      <article
                        key={`${evento?.tipo}-${dados?.ID_EXECUCAO_ITEM || dados?.ID_PRODUTO_REGRA_SANEADA || index}`}
                        className={`sf-timeline-item ${processamento ? "is-processing" : "is-history"}`}
                      >
                        <div className="sf-timeline-marker" />
                        <div className="sf-timeline-card">
                          <div className="sf-timeline-head">
                            <div>
                              <span>{processamento ? "Processamento" : "Histórico de saneamento"}</span>
                              <strong>{fmtDate(evento?.data)}</strong>
                            </div>
                            <StatusBadge status={processamento ? dados?.DM_STATUS_DEPOIS : dados?.DM_STATUS} />
                          </div>

                          {processamento ? (
                            <div className="sf-timeline-grid">
                              <InfoItem label="Status antes" value={dados?.DM_STATUS_ANTES} />
                              <InfoItem label="Status depois" value={dados?.DM_STATUS_DEPOIS} />
                              <InfoItem label="Origem" value={dados?.ORIGEM_PROCESSAMENTO} />
                              <InfoItem label="Tentou IMendes" value={dados?.TENTOU_IMENDES === "S" ? "Sim" : "Não"} />
                              <InfoItem label="Consulta online" value={dados?.CONSULTA_ONLINE_IMENDES === "S" ? "Sim" : "Não"} />
                              <InfoItem label="Cache" value={dados?.UTILIZOU_CACHE === "S" ? "Sim" : "Não"} />
                              <InfoItem label="HTTP IMendes" value={dados?.HTTP_STATUS_IMENDES} />
                              <InfoItem label="Execução" value={dados?.ID_EXECUCAO ? `#${dados.ID_EXECUCAO}` : "-"} />
                              <div className="sf-timeline-message">
                                <span>Motivo / erro</span>
                                <p>{safeText(dados?.MOTIVO_PROCESSAMENTO || dados?.ERRO)}</p>
                              </div>
                            </div>
                          ) : (
                            <div className="sf-timeline-grid">
                              <InfoItem label="Histórico" value={dados?.ID_PRODUTO_REGRA_SANEADA ? `#${dados.ID_PRODUTO_REGRA_SANEADA}` : "-"} />
                              <InfoItem label="Evento" value={dados?.TIPO_EVENTO} />
                              <InfoItem label="NCM original" value={dados?.CD_NCM_ORIGINAL} />
                              <InfoItem label="NCM IMendes" value={dados?.CD_NCM_IMENDES} />
                              <InfoItem label="Última consulta" value={fmtDate(dados?.DATA_ULTIMA_CONSULTA_IMENDES)} />
                              <InfoItem label="Próxima tentativa" value={fmtDate(dados?.DATA_PROXIMA_CONSULTA_PERMITIDA)} />
                              <div className="sf-timeline-message">
                                <span>Motivo</span>
                                <p>{safeText(dados?.MOTIVO_STATUS)}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="sf-empty">
                  <FiFileText />
                  <strong>Nenhum histórico encontrado</strong>
                  <small>Este produto ainda não possui eventos rastreáveis.</small>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function SaneamentoFiscal() {
  const usuario = useMemo(() => obterUsuarioSessao(), []);
  const avatar = useMemo(() => iniciais(usuario.nome), [usuario.nome]);
  const monitorTimerRef = useRef(null);

  const [licencas, setLicencas] = useState([]);
  const [nrLicenca, setNrLicenca] = useState("");
  const [clienteResumo, setClienteResumo] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [produtos, setProdutos] = useState({ page: 1, limit: 30, total: 0, data: [] });
  const [pendencias, setPendencias] = useState({ page: 1, limit: 30, total: 0, data: [] });
  const [execucoes, setExecucoes] = useState({ page: 1, limit: 20, total: 0, data: [] });

  const [observabilidade, setObservabilidade] = useState(null);
  const [observabilidadeDias, setObservabilidadeDias] = useState({ data: [] });
  const [observabilidadeErros, setObservabilidadeErros] = useState({
    page: 1,
    limit: 50,
    total: 0,
    data: [],
  });
  const [buscaErroObservabilidade, setBuscaErroObservabilidade] = useState("");
  const [somenteReprocessados, setSomenteReprocessados] = useState(false);
  const [historicoProduto, setHistoricoProduto] = useState(null);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);

  const [aba, setAba] = useState("observabilidade");
  const [statusProduto, setStatusProduto] = useState("");
  const [statusPendencia, setStatusPendencia] = useState("");
  const [searchProduto, setSearchProduto] = useState("");
  const [loading, setLoading] = useState(false);
  const [processando, setProcessando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [automaticoAtivo, setAutomaticoAtivo] = useState(false);
  const [automaticoStatus, setAutomaticoStatus] = useState(null);
  const [carregandoAutomatico, setCarregandoAutomatico] = useState(false);
  const [buscaLicenca, setBuscaLicenca] = useState("");
  const [filtroEsteira, setFiltroEsteira] = useState("todas");
  const [erro, setErro] = useState("");
  const [logs, setLogs] = useState([]);
  const [ultimoResultado, setUltimoResultado] = useState(null);
  const [statusOperacao, setStatusOperacao] = useState({
    status: "PARADO",
    tipo: "",
    mensagem: "Nenhum processamento em execução.",
    detalhe: "",
    iniciadoEm: null,
    atualizadoEm: null,
  });
  const [ultimaAtualizacaoMonitor, setUltimaAtualizacaoMonitor] = useState(null);

  const [configLote, setConfigLote] = useState({
    limitPorCiclo: 5,
    maxCiclos: 1,
    maxConsultas: 5,
    intervaloSegundos: 60,
  });

  const [modalManual, setModalManual] = useState(null);
  const [formManual, setFormManual] = useState(FORM_MANUAL_INICIAL);
  const [salvandoManual, setSalvandoManual] = useState(false);

  const empresaPrincipal = clienteResumo?.empresaPrincipal || null;
  const cliente = clienteResumo?.cliente || null;
  const saneamento = clienteResumo?.saneamento || null;

  const obsImendes = observabilidade?.imendes || {};
  const obsProcessamento = observabilidade?.processamento || {};
  const obsGestao = observabilidade?.gestao || {};
  const obsFila = observabilidade?.fila || {};
  const obsEsteira = observabilidade?.esteira || {};
  const obsAlertas = Array.isArray(observabilidade?.alertas) ? observabilidade.alertas : [];

  const consumoHoje = Number(
    obsImendes?.consumidasOnlineHoje ??
      saneamento?.CONSUMO_HOJE ??
      dashboard?.licenca?.CONSUMO_HOJE ??
      0
  );
  const saldoHoje = Number(
    obsImendes?.saldoHoje ??
      saneamento?.SALDO_HOJE ??
      dashboard?.licenca?.SALDO_HOJE ??
      0
  );
  const limiteHoje = Number(
    obsImendes?.limiteDia ??
      saneamento?.LIMITE_CONSULTAS_DIA ??
      dashboard?.licenca?.LIMITE_CONSULTAS_DIA ??
      500
  );
  const percentualConsumo = limiteHoje > 0
    ? Math.min(100, Math.round((consumoHoje / limiteHoje) * 100))
    : 0;
  const hubHoje = Number(
    obsGestao?.viaHubHoje ??
      obsImendes?.hubNoPeriodo ??
      obsProcessamento?.saneadosViaHub ??
      0
  );
  const cacheHoje = Number(
    obsGestao?.viaCacheHoje ??
      obsImendes?.cacheNoPeriodo ??
      obsProcessamento?.resolvidosViaCache ??
      0
  );
  const onlineHoje = Number(
    obsGestao?.consultasOnlineHoje ??
      obsImendes?.consultasOnlineNoPeriodo ??
      consumoHoje ??
      0
  );
  const consultasEvitadasHoje = Number(
    obsGestao?.consultasEvitadasHoje ??
      obsImendes?.consultasEvitadasNoPeriodo ??
      (hubHoje + cacheHoje)
  );
  const totalCanaisFiscaisHoje = consultasEvitadasHoje + onlineHoje;
  const taxaEconomiaConsultasHoje = Number(
    obsGestao?.taxaEconomiaConsultasHoje ??
      obsImendes?.taxaEconomiaConsultasNoPeriodo ??
      (totalCanaisFiscaisHoje > 0
        ? (consultasEvitadasHoje / totalCanaisFiscaisHoje) * 100
        : 0)
  );
  const quotaOnlineEsgotada =
    obsGestao?.quotaOnlineEsgotada === true ||
    obsImendes?.quotaOnlineEsgotada === true ||
    (limiteHoje > 0 && saldoHoje <= 0);
  const errosHoje = Number(obsGestao?.errosHoje ?? obsProcessamento?.erros ?? 0);
  const statusGestor = errosHoje > 0
    ? "ATENÇÃO"
    : quotaOnlineEsgotada
      ? "HUB/CACHE ATIVOS"
      : "NORMAL";
  const ultimaExecucao = Array.isArray(execucoes.data) && execucoes.data.length ? execucoes.data[0] : null;
  const automaticoBackendAtivo = automaticoStatus?.ativo === true || automaticoAtivo;
  const automaticoExecutandoAgora = automaticoStatus?.executandoAgora === true;
  const textoStatusAutomatico = automaticoStatus?.status || (automaticoBackendAtivo ? "LIGADO" : "DESLIGADO");
  const operacaoVisualAtiva =
    processando ||
    sincronizando ||
    automaticoBackendAtivo ||
    ["PROCESSANDO", "MONITORANDO", "SINCRONIZANDO", "AUTOMATICO_BACKEND"].includes(statusOperacao.status);

  const totalEsteirasAtivas = useMemo(
    () => licencas.filter((licenca) => isLicencaComEsteiraAtiva(licenca)).length,
    [licencas]
  );

  const licencasFiltradas = useMemo(() => {
    const termo = toStr(buscaLicenca).toLowerCase();

    return licencas.filter((licenca) => {
      const ativa = isLicencaComEsteiraAtiva(licenca);

      if (filtroEsteira === "ativas" && !ativa) return false;
      if (filtroEsteira === "inativas" && ativa) return false;

      if (!termo) return true;
      return textoBuscaLicenca(licenca).includes(termo);
    });
  }, [licencas, buscaLicenca, filtroEsteira]);

  const licencaSelecionada = useMemo(
    () => licencas.find((licenca) => String(licenca.NR_LICENCA) === String(nrLicenca)) || null,
    [licencas, nrLicenca]
  );

  const licencasSelect = useMemo(() => {
    if (!nrLicenca) return licencasFiltradas;

    const existeSelecionada = licencasFiltradas.some(
      (licenca) => String(licenca.NR_LICENCA) === String(nrLicenca)
    );

    if (existeSelecionada) return licencasFiltradas;

    const selecionada = licencas.find(
      (licenca) => String(licenca.NR_LICENCA) === String(nrLicenca)
    );

    return selecionada ? [selecionada, ...licencasFiltradas] : licencasFiltradas;
  }, [licencas, licencasFiltradas, nrLicenca]);

  function atualizarLicencaLocalAutomatico(licenca, ativo, patch = {}) {
    const chave = String(licenca || "");

    setLicencas((prev) =>
      prev.map((item) =>
        String(item.NR_LICENCA) === chave
          ? {
              ...item,
              ...patch,
              MODO_AUTOMATICO: ativo ? "S" : "N",
            }
          : item
      )
    );
  }

  function addLog(tipo, mensagem, payload = null) {
    setLogs((prev) => [
      {
        id: `${Date.now()}-${Math.random()}`,
        tipo,
        mensagem,
        payload,
        data: new Date().toISOString(),
      },
      ...prev,
    ].slice(0, 120));
  }

  async function atualizarMonitoramentoRapido(licenca = nrLicenca) {
    if (!licenca) return;

    try {
      await Promise.all([
        carregarDashboard(licenca),
        carregarPendencias(licenca),
        carregarExecucoes(licenca),
        carregarObservabilidade(licenca),
        carregarStatusAutomatico(licenca, true),
      ]);
      setUltimaAtualizacaoMonitor(new Date().toISOString());
    } catch (error) {
      addLog(
        "erro",
        "Falha ao atualizar monitoramento do processamento.",
        error?.response?.data || error.message
      );
    }
  }


  async function carregarStatusAutomatico(licenca = nrLicenca, silencioso = false) {
    if (!licenca) {
      setAutomaticoStatus(null);
      setAutomaticoAtivo(false);
      return null;
    }

    try {
      const json = await apiGet(`/ssw/hub-fiscal-produtos/saneamento/${licenca}/automatico/status`);
      const result = unwrapResult(json, null);
      setAutomaticoStatus(result);
      setAutomaticoAtivo(result?.ativo === true);
      atualizarLicencaLocalAutomatico(licenca, result?.ativo === true, {
        MODO_AUTOMATICO: result?.ativo === true ? "S" : "N",
        STATUS_AUTOMATICO_SERVIDOR: result?.status || null,
        TIMER_AUTOMATICO_ATIVO: result?.timerAtivo ? "S" : "N",
        EXECUTANDO_AGORA: result?.executandoAgora ? "S" : "N",
      });

      if (!silencioso) {
        addLog(
          result?.ativo ? "auto" : "info",
          result?.ativo
            ? `Esteira automática ligada no backend para a licença ${licenca}.`
            : `Esteira automática desligada para a licença ${licenca}.`,
          result
        );
      }

      return result;
    } catch (error) {
      if (!silencioso) {
        addLog(
          "erro",
          "Falha ao consultar status da esteira automática.",
          error?.response?.data || error.message
        );
      }
      return null;
    }
  }

  async function carregarLicencas() {
    setErro("");
    try {
      const json = await apiGet("/ssw/hub-fiscal-produtos/saneamento/licencas", {
        ativo: "S",
      });
      const result = unwrapResult(json, {});
      const rows = Array.isArray(result?.data) ? result.data : Array.isArray(json?.data) ? json.data : [];
      setLicencas(rows);

      if (!nrLicenca && rows.length) {
        setNrLicenca(String(rows[0].NR_LICENCA || ""));
      }
    } catch (error) {
      setErro(error?.response?.data?.message || error.message || "Falha ao carregar licenças.");
      showError("Falha ao carregar licenças com saneamento habilitado.");
    }
  }

  async function carregarClienteLicenca(licenca) {
    if (!licenca) {
      setClienteResumo(null);
      return;
    }

    try {
      const json = await apiGet(`/ssw/cliente/por-licenca/${licenca}/saneamento-resumo`);
      const result = unwrapResult(json, null);
      setClienteResumo(result);
      addLog("info", `Dados do cliente carregados para a licença ${licenca}.`, result?.cliente || null);
    } catch (error) {
      setClienteResumo(null);
      addLog("erro", `Não foi possível carregar dados do cliente da licença ${licenca}.`, error?.response?.data || error.message);
    }
  }

  async function carregarDashboard(licenca) {
    if (!licenca) {
      setDashboard(null);
      return;
    }

    const json = await apiGet(`/ssw/hub-fiscal-produtos/saneamento/${licenca}/dashboard`);
    setDashboard(unwrapResult(json, null));
  }

  async function carregarProdutos(licenca = nrLicenca) {
    if (!licenca) return;

    const json = await apiGet(`/ssw/hub-fiscal-produtos/saneamento/${licenca}/produtos`, {
      page: produtos.page || 1,
      limit: produtos.limit || 30,
      status: statusProduto || undefined,
      produto: searchProduto || undefined,
    });

    setProdutos({
      page: Number(json?.page || 1),
      limit: Number(json?.limit || 30),
      total: Number(json?.total || 0),
      data: Array.isArray(json?.data) ? json.data : [],
    });
  }

  async function carregarPendencias(licenca = nrLicenca) {
    if (!licenca) return;

    const json = await apiGet("/ssw/hub-fiscal-produtos/saneamento/pendencias-imendes", {
      nrLicenca: licenca,
      status: statusPendencia || undefined,
      page: pendencias.page || 1,
      limit: pendencias.limit || 30,
    });

    const result = unwrapResult(json, json || {});
    setPendencias({
      page: Number(result?.page || 1),
      limit: Number(result?.limit || 30),
      total: Number(result?.total || 0),
      data: Array.isArray(result?.data) ? result.data : [],
    });
  }

  async function carregarExecucoes(licenca = nrLicenca) {
    if (!licenca) return;

    const json = await apiGet(`/ssw/hub-fiscal-produtos/saneamento/${licenca}/execucoes`, {
      page: execucoes.page || 1,
      limit: execucoes.limit || 20,
    });

    setExecucoes({
      page: Number(json?.page || 1),
      limit: Number(json?.limit || 20),
      total: Number(json?.total || 0),
      data: Array.isArray(json?.data) ? json.data : [],
    });
  }

  async function carregarObservabilidade(licenca = nrLicenca) {
    if (!licenca) {
      setObservabilidade(null);
      return null;
    }

    const json = await apiGet(
      `/ssw/hub-fiscal-produtos/saneamento/${licenca}/observabilidade`
    );
    const result = unwrapResult(json, null);
    setObservabilidade(result);
    return result;
  }

  async function carregarObservabilidadeDias(licenca = nrLicenca) {
    if (!licenca) {
      setObservabilidadeDias({ data: [] });
      return null;
    }

    const json = await apiGet(
      `/ssw/hub-fiscal-produtos/saneamento/${licenca}/observabilidade/dias`,
      { dias: 30 }
    );
    const result = unwrapResult(json, { data: [] });
    setObservabilidadeDias({
      ...result,
      data: Array.isArray(result?.data) ? result.data : [],
    });
    return result;
  }

  async function carregarObservabilidadeErros(licenca = nrLicenca) {
    if (!licenca) {
      setObservabilidadeErros({ page: 1, limit: 50, total: 0, data: [] });
      return null;
    }

    const json = await apiGet(
      `/ssw/hub-fiscal-produtos/saneamento/${licenca}/observabilidade/erros`,
      {
        page: 1,
        limit: 50,
        search: buscaErroObservabilidade || undefined,
        SOMENTE_REPROCESSADOS: somenteReprocessados ? "S" : "N",
      }
    );
    const result = unwrapResult(json, {});
    setObservabilidadeErros({
      page: Number(result?.page || 1),
      limit: Number(result?.limit || 50),
      total: Number(result?.total || 0),
      data: Array.isArray(result?.data) ? result.data : [],
    });
    return result;
  }

  async function abrirHistoricoProduto(idProduto) {
    if (!nrLicenca || !idProduto || carregandoHistorico) return;

    setCarregandoHistorico(true);
    setHistoricoProduto({
      NR_LICENCA: nrLicenca,
      ID_PRODUTO_SERVICO: idProduto,
      produtoAtual: null,
      eventos: [],
    });

    try {
      const json = await apiGet(
        `/ssw/hub-fiscal-produtos/saneamento/${nrLicenca}/produtos/${idProduto}/historico`,
        { page: 1, limit: 100 }
      );
      const result = unwrapResult(json, null);
      setHistoricoProduto(result);
    } catch (error) {
      const message = obterMensagemErro(error, "Falha ao carregar histórico do produto.");
      setHistoricoProduto(null);
      showError(message);
    } finally {
      setCarregandoHistorico(false);
    }
  }

  async function carregarTudo(licenca = nrLicenca, silencioso = false) {
    if (!licenca) return;

    setLoading(true);
    setErro("");

    try {
      await Promise.all([
        carregarClienteLicenca(licenca),
        carregarDashboard(licenca),
        carregarProdutos(licenca),
        carregarPendencias(licenca),
        carregarExecucoes(licenca),
        carregarObservabilidade(licenca),
        carregarObservabilidadeDias(licenca),
        carregarObservabilidadeErros(licenca),
        carregarStatusAutomatico(licenca, true),
      ]);

      if (!silencioso) addLog("sucesso", `Tela atualizada para a licença ${licenca}.`);
    } catch (error) {
      const message = error?.response?.data?.message || error.message || "Falha ao atualizar tela.";
      setErro(message);
      addLog("erro", message, error?.response?.data || null);
    } finally {
      setLoading(false);
    }
  }

  async function sincronizarProdutos() {
    if (!nrLicenca || sincronizando) return;

    setSincronizando(true);
    setErro("");
    setAba("processamento");
    setStatusOperacao({
      status: "SINCRONIZANDO",
      tipo: "SINCRONIZACAO",
      mensagem: "Sincronizando produtos da licença selecionada.",
      detalhe: "Aguarde. A tela será atualizada automaticamente ao final.",
      iniciadoEm: new Date().toISOString(),
      atualizadoEm: new Date().toISOString(),
    });
    addLog("info", `Sincronizando produtos da licença ${nrLicenca}.`);

    try {
      const idEmpresa = empresaPrincipal?.id || empresaPrincipal?.ID_EMPRESA || saneamento?.ID_EMPRESA_PADRAO || null;
      const json = await apiPost(`/ssw/hub-fiscal-produtos/saneamento/${nrLicenca}/sincronizar-produtos`, {
        ID_EMPRESA: idEmpresa,
        somenteAtivos: true,
        usuario: usuario.nome,
      });

      const result = unwrapResult(json, json);
      setStatusOperacao({
        status: "FINALIZADO",
        tipo: "SINCRONIZACAO",
        mensagem: "Sincronização finalizada com sucesso.",
        detalhe: "Produtos carregados para o controle de saneamento.",
        iniciadoEm: statusOperacao.iniciadoEm,
        atualizadoEm: new Date().toISOString(),
      });
      addLog("sucesso", "Produtos sincronizados com sucesso.", result);
      showInfo("Produtos sincronizados para saneamento fiscal.");
      await carregarTudo(nrLicenca, true);
    } catch (error) {
      const message = obterMensagemErro(error, "Falha ao sincronizar produtos.");
      setStatusOperacao({
        status: "ERRO",
        tipo: "SINCRONIZACAO",
        mensagem: message,
        detalhe: "A sincronização não foi concluída.",
        iniciadoEm: statusOperacao.iniciadoEm,
        atualizadoEm: new Date().toISOString(),
      });
      setErro(message);
      addLog("erro", message, error?.response?.data || null);
      showError(message);
    } finally {
      setSincronizando(false);
    }
  }

  async function processarRodada({ automatico = false } = {}) {
    if (!nrLicenca || processando) {
      if (processando) {
        addLog(
          "info",
          "Já existe uma rodada em andamento. A nova chamada foi ignorada para evitar concorrência."
        );
      }
      return null;
    }

    setProcessando(true);
    setErro("");
    setAba("processamento");

    const iniciadoEm = new Date().toISOString();
    const body = {
      usuario: automatico ? `automatico-${usuario.nome}` : usuario.nome,
      consultarImendes: true,
      limitPorCiclo: Number(configLote.limitPorCiclo || 5),
      maxCiclos: Number(configLote.maxCiclos || 1),
      maxConsultas: Number(configLote.maxConsultas || 5),
    };

    setStatusOperacao({
      status: "PROCESSANDO",
      tipo: automatico ? "AUTOMATICO" : "MANUAL",
      mensagem: automatico
        ? `Processamento automático em execução para a licença ${nrLicenca}.`
        : `Rodada manual em execução para a licença ${nrLicenca}.`,
      detalhe:
        "Aguarde. A API está processando o lote e a tela está monitorando dashboard, pendências e execuções.",
      iniciadoEm,
      atualizadoEm: iniciadoEm,
    });

    setUltimoResultado({
      status: "EM_EXECUCAO",
      nrLicenca,
      iniciadoEm,
      payload: body,
      mensagem: "Solicitação enviada para a API. Aguardando retorno do lote diário.",
    });

    addLog(
      automatico ? "auto" : "info",
      `Iniciando rodada de processamento da licença ${nrLicenca}.`,
      body
    );

    try {
      const json = await apiPost(
        `/ssw/hub-fiscal-produtos/saneamento/${nrLicenca}/processar-lote-diario`,
        body,
        {
          timeout: PROCESSAMENTO_TIMEOUT_MS,
        }
      );

      const result = unwrapResult(json, json);
      setUltimoResultado(result);
      setStatusOperacao({
        status: "FINALIZADO",
        tipo: automatico ? "AUTOMATICO" : "MANUAL",
        mensagem: "Rodada finalizada com retorno da API.",
        detalhe: "Dashboard, pendências e execuções foram atualizados após o processamento.",
        iniciadoEm,
        atualizadoEm: new Date().toISOString(),
      });
      addLog("sucesso", "Rodada finalizada com retorno da API.", result);
      await carregarTudo(nrLicenca, true);
      return result;
    } catch (error) {
      const message = obterMensagemErro(error, "Falha ao processar lote.");

      if (isTimeoutError(error)) {
        const aviso =
          "A tela atingiu o tempo limite aguardando a resposta, mas o backend pode continuar processando. Vou manter o monitoramento ativo para você acompanhar pelas execuções e indicadores.";

        setStatusOperacao({
          status: "MONITORANDO",
          tipo: automatico ? "AUTOMATICO" : "MANUAL",
          mensagem: aviso,
          detalhe: `Timeout da tela após ${Math.round(PROCESSAMENTO_TIMEOUT_MS / 1000)} segundos. Isso não confirma falha no backend.`,
          iniciadoEm,
          atualizadoEm: new Date().toISOString(),
        });
        setErro("");
        setUltimoResultado((prev) => ({
          ...(prev || {}),
          status: "MONITORANDO_APOS_TIMEOUT",
          mensagem: aviso,
          erroOriginal: message,
          atualizadoEm: new Date().toISOString(),
        }));
        addLog("aviso", aviso, { erroOriginal: message, payload: body });
        showInfo("Processamento ainda pode estar rodando. Monitoramento mantido na tela.");
        await atualizarMonitoramentoRapido(nrLicenca);
        return null;
      }

      setStatusOperacao({
        status: "ERRO",
        tipo: automatico ? "AUTOMATICO" : "MANUAL",
        mensagem: message,
        detalhe: "A rodada retornou erro antes de finalizar.",
        iniciadoEm,
        atualizadoEm: new Date().toISOString(),
      });
      setErro(message);
      addLog("erro", message, error?.response?.data || null);
      showError(message);
      return null;
    } finally {
      setProcessando(false);
    }
  }


  async function iniciarAutomatico() {
    if (!nrLicenca || automaticoBackendAtivo || carregandoAutomatico) return;

    setCarregandoAutomatico(true);
    setErro("");
    setAba("processamento");

    const iniciadoEm = new Date().toISOString();
    const body = {
      usuario: usuario.nome,
      consultarImendes: true,
      executarAgora: true,
      limitPorCiclo: Number(configLote.limitPorCiclo || 5),
      maxCiclos: Number(configLote.maxCiclos || 1),
      maxConsultas: Number(configLote.maxConsultas || 5),
      intervaloSegundos: Number(configLote.intervaloSegundos || 60),
    };

    setStatusOperacao({
      status: "AUTOMATICO_BACKEND",
      tipo: "ESTEIRA NO SERVIDOR",
      mensagem: "Ligando a esteira automática no backend.",
      detalhe:
        "Depois de ligada, ela continua processando mesmo se você sair desta tela. A tela apenas monitora o que o backend está fazendo.",
      iniciadoEm,
      atualizadoEm: iniciadoEm,
    });

    setUltimoResultado({
      status: "LIGANDO_ESTEIRA_BACKEND",
      nrLicenca,
      payload: body,
      iniciadoEm,
      mensagem: "Solicitando início da esteira automática no servidor.",
    });

    addLog("auto", `Ligando esteira automática no backend para a licença ${nrLicenca}.`, body);

    try {
      const json = await apiPost(
        `/ssw/hub-fiscal-produtos/saneamento/${nrLicenca}/automatico/iniciar`,
        body,
        { timeout: 30000 }
      );

      const result = unwrapResult(json, json);
      setAutomaticoStatus(result);
      setAutomaticoAtivo(true);
      atualizarLicencaLocalAutomatico(nrLicenca, true, {
        STATUS_AUTOMATICO_SERVIDOR: result?.status || result?.estado?.status || "LIGADO",
        TIMER_AUTOMATICO_ATIVO: result?.timerAtivo ? "S" : "N",
        EXECUTANDO_AGORA: result?.executandoAgora ? "S" : "N",
      });
      setUltimoResultado(result);
      setStatusOperacao({
        status: "AUTOMATICO_BACKEND",
        tipo: "ESTEIRA NO SERVIDOR",
        mensagem: "Esteira automática ligada no backend.",
        detalhe:
          "Você pode sair desta tela. O backend continuará executando ciclos até você clicar em Parar esteira.",
        iniciadoEm,
        atualizadoEm: new Date().toISOString(),
      });
      addLog("sucesso", "Esteira automática ligada no backend.", result);
      showInfo("Esteira automática ligada no backend.");
      await atualizarMonitoramentoRapido(nrLicenca);
    } catch (error) {
      const message = obterMensagemErro(error, "Falha ao iniciar esteira automática.");
      setErro(message);
      setAutomaticoAtivo(false);
      setStatusOperacao({
        status: "ERRO",
        tipo: "ESTEIRA NO SERVIDOR",
        mensagem: message,
        detalhe: "A esteira automática não foi ligada.",
        iniciadoEm,
        atualizadoEm: new Date().toISOString(),
      });
      addLog("erro", message, error?.response?.data || null);
      showError(message);
    } finally {
      setCarregandoAutomatico(false);
    }
  }

  async function pararAutomatico() {
    if (!nrLicenca || carregandoAutomatico) return;

    setCarregandoAutomatico(true);
    setErro("");
    addLog("info", `Solicitando parada da esteira automática da licença ${nrLicenca}.`);

    try {
      const json = await apiPost(
        `/ssw/hub-fiscal-produtos/saneamento/${nrLicenca}/automatico/parar`,
        { usuario: usuario.nome },
        { timeout: 30000 }
      );

      const result = unwrapResult(json, json);
      setAutomaticoStatus(result);
      setAutomaticoAtivo(false);
      atualizarLicencaLocalAutomatico(nrLicenca, false, {
        STATUS_AUTOMATICO_SERVIDOR: result?.status || result?.estado?.status || "DESLIGADO",
        TIMER_AUTOMATICO_ATIVO: "N",
        EXECUTANDO_AGORA: "N",
      });
      setStatusOperacao({
        status: "PARADO",
        tipo: "ESTEIRA NO SERVIDOR",
        mensagem: "Esteira automática parada pelo operador.",
        detalhe: "Nenhum novo ciclo automático será iniciado no backend para esta licença.",
        iniciadoEm: null,
        atualizadoEm: new Date().toISOString(),
      });
      setUltimoResultado(result);
      addLog("sucesso", "Esteira automática parada no backend.", result);
      showInfo("Esteira automática parada.");
      await atualizarMonitoramentoRapido(nrLicenca);
    } catch (error) {
      const message = obterMensagemErro(error, "Falha ao parar esteira automática.");
      setErro(message);
      addLog("erro", message, error?.response?.data || null);
      showError(message);
    } finally {
      setCarregandoAutomatico(false);
    }
  }

  function abrirManual(row) {
    setModalManual(row);
    setFormManual(montarFormManual(row));
  }

  async function salvarManual() {
    if (!modalManual?.ID_PRODUTO_REGRA_SANEADA || salvandoManual) return;

    setSalvandoManual(true);

    try {
      const payload = {
        ...normalizarPayloadManual(formManual),
        usuario: usuario.nome,
      };

      const json = await apiPost(
        `/ssw/hub-fiscal-produtos/saneamento/pendencias-imendes/${modalManual.ID_PRODUTO_REGRA_SANEADA}/sanear-manual`,
        payload
      );

      const result = unwrapResult(json, json);
      addLog("sucesso", "Produto saneado manualmente e aplicado na regra final.", result);
      showInfo("Produto saneado manualmente e aplicado na regra final.");
      setModalManual(null);
      await carregarTudo(nrLicenca, true);
    } catch (error) {
      const message = error?.response?.data?.message || error.message || "Falha ao sanear manualmente.";
      addLog("erro", message, error?.response?.data || null);
      showError(message);
    } finally {
      setSalvandoManual(false);
    }
  }

  useEffect(() => {
    document.title = "Central de saneamento fiscal";
    carregarLicencas();
    return () => {
if (monitorTimerRef.current) window.clearInterval(monitorTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (nrLicenca) {
      carregarTudo(nrLicenca);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nrLicenca]);

  useEffect(() => {
    if (nrLicenca) carregarProdutos(nrLicenca);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusProduto, produtos.page, produtos.limit]);

  useEffect(() => {
    if (nrLicenca) carregarPendencias(nrLicenca);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusPendencia, pendencias.page, pendencias.limit]);

  useEffect(() => {
    if (nrLicenca && aba === "observabilidade") {
      carregarObservabilidadeDias(nrLicenca);
      carregarObservabilidadeErros(nrLicenca);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nrLicenca, aba, somenteReprocessados]);

  useEffect(() => {
    if (monitorTimerRef.current) {
      window.clearInterval(monitorTimerRef.current);
      monitorTimerRef.current = null;
    }

    const deveMonitorar =
      nrLicenca &&
      (processando ||
        automaticoBackendAtivo ||
        ["PROCESSANDO", "MONITORANDO", "SINCRONIZANDO"].includes(statusOperacao.status));

    if (!deveMonitorar) return undefined;

    monitorTimerRef.current = window.setInterval(() => {
      atualizarMonitoramentoRapido(nrLicenca);
    }, MONITORAMENTO_PROCESSAMENTO_MS);

    return () => {
      if (monitorTimerRef.current) {
        window.clearInterval(monitorTimerRef.current);
        monitorTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nrLicenca, processando, automaticoBackendAtivo, statusOperacao.status]);

  return (
    <MainLayout>
      <div className="saneamento-fiscal-page">
        <div className="sf-shell">
          <header className="sf-header">
            <div className="sf-header-copy">
              <span className="sf-eyebrow">Central interna</span>
              <h1>Saneamento fiscal IMendes</h1>
              <p>
                Selecione uma licença habilitada, ligue a esteira automática no backend, acompanhe execuções, consumo diário e trate manualmente as pendências quando necessário.
              </p>
            </div>

            <div className="sf-user-card">
              <div className="sf-user-avatar">{avatar}</div>
              <div>
                <span>Operador</span>
                <strong>{usuario.nome}</strong>
                <small>{usuario.id ? `Funcionário #${usuario.id}` : "Sessão interna"}</small>
              </div>
              <span className="sf-online" />
            </div>
          </header>

          {erro ? <div className="sf-alert">{erro}</div> : null}

          <section className="sf-control-card">
            <div className="sf-control-main">
              <div className="sf-license-picker">
                <div className="sf-license-picker-head">
                  <label className="sf-field sf-license-search">
                    <span>Buscar cliente ou licença</span>
                    <div className="sf-search-input">
                      <FiSearch />
                      <input
                        value={buscaLicenca}
                        onChange={(e) => setBuscaLicenca(e.target.value)}
                        placeholder="Digite licença, cliente, CNPJ ou observação"
                      />
                    </div>
                  </label>

                  <div className="sf-license-segments" aria-label="Filtro de esteira">
                    <button
                      type="button"
                      className={filtroEsteira === "todas" ? "active" : ""}
                      onClick={() => setFiltroEsteira("todas")}
                    >
                      Todas
                    </button>
                    <button
                      type="button"
                      className={filtroEsteira === "ativas" ? "active is-on" : ""}
                      onClick={() => setFiltroEsteira("ativas")}
                    >
                      Ativas
                    </button>
                    <button
                      type="button"
                      className={filtroEsteira === "inativas" ? "active" : ""}
                      onClick={() => setFiltroEsteira("inativas")}
                    >
                      Inativas
                    </button>
                  </div>
                </div>

                <label className="sf-field">
                  <span>Licença com direito ao saneamento</span>
                  <select value={nrLicenca} onChange={(e) => setNrLicenca(e.target.value)} disabled={loading}>
                    <option value="">Selecione uma licença</option>
                    {licencasSelect.map((licenca) => {
                      const ativa = isLicencaComEsteiraAtiva(licenca);
                      const nome = getNomeLicenca(licenca);
                      const documento = getDocumentoLicenca(licenca);

                      return (
                        <option key={licenca.NR_LICENCA} value={licenca.NR_LICENCA}>
                          {ativa ? "🟢" : "⚪"} {licenca.NR_LICENCA} • limite {fmtInt(licenca.LIMITE_CONSULTAS_DIA || 500)}/dia{nome ? ` • ${nome}` : ""}{documento ? ` • ${documento}` : ""}
                        </option>
                      );
                    })}
                  </select>
                </label>

                <div className="sf-license-summary">
                  <span>{fmtInt(licencasFiltradas.length)} licença(s) exibida(s)</span>
                  <strong>{fmtInt(totalEsteirasAtivas)} com esteira ligada</strong>
                  {licencaSelecionada ? (
                    <em className={automaticoBackendAtivo || isLicencaComEsteiraAtiva(licencaSelecionada) ? "is-on" : "is-off"}>
                      {automaticoBackendAtivo || isLicencaComEsteiraAtiva(licencaSelecionada) ? "Selecionada com esteira ativa" : "Selecionada sem esteira ativa"}
                    </em>
                  ) : null}
                </div>
              </div>

              <div className="sf-control-actions">
                <button className="sf-btn sf-btn--soft" type="button" onClick={() => carregarTudo(nrLicenca)} disabled={!nrLicenca || loading}>
                  <FiRefreshCw />
                  Atualizar
                </button>

                <button className="sf-btn sf-btn--soft" type="button" onClick={sincronizarProdutos} disabled={!nrLicenca || sincronizando || automaticoBackendAtivo}>
                  <FiDatabase />
                  {sincronizando ? "Sincronizando..." : "Sincronizar produtos"}
                </button>

                {!automaticoBackendAtivo ? (
                  <button className="sf-btn sf-btn--primary" type="button" onClick={iniciarAutomatico} disabled={!nrLicenca || processando || carregandoAutomatico}>
                    <FiZap />
                    {carregandoAutomatico ? "Ligando..." : "Ligar esteira"}
                  </button>
                ) : (
                  <button className="sf-btn sf-btn--danger" type="button" onClick={pararAutomatico} disabled={carregandoAutomatico}>
                    <FiStopCircle />
                    {carregandoAutomatico ? "Parando..." : "Parar esteira"}
                  </button>
                )}
              </div>
            </div>

            <div className="sf-lote-grid">
              <label className="sf-field">
                <span>Produtos por ciclo</span>
                <input type="number" min="1" max="100" value={configLote.limitPorCiclo} onChange={(e) => setConfigLote((p) => ({ ...p, limitPorCiclo: e.target.value }))} />
              </label>
              <label className="sf-field">
                <span>Máx. ciclos</span>
                <input type="number" min="1" max="20" value={configLote.maxCiclos} onChange={(e) => setConfigLote((p) => ({ ...p, maxCiclos: e.target.value }))} />
              </label>
              <label className="sf-field">
                <span>Máx. consultas</span>
                <input type="number" min="1" max="500" value={configLote.maxConsultas} onChange={(e) => setConfigLote((p) => ({ ...p, maxConsultas: e.target.value }))} />
              </label>
              <label className="sf-field">
                <span>Intervalo automático</span>
                <input type="number" min="30" max="3600" value={configLote.intervaloSegundos} onChange={(e) => setConfigLote((p) => ({ ...p, intervaloSegundos: e.target.value }))} />
              </label>
              <button className="sf-btn sf-btn--blue" type="button" onClick={() => processarRodada()} disabled={!nrLicenca || processando || automaticoBackendAtivo}>
                <FiPlay />
                {processando ? "Rodada em andamento..." : "Rodar uma rodada"}
              </button>
            </div>
          </section>

          {operacaoVisualAtiva ? (
            <section className={`sf-processing-banner sf-processing-banner--${statusOperacao.status.toLowerCase()}`}>
              <div className="sf-processing-pulse">
                <FiRefreshCw />
              </div>

              <div className="sf-processing-copy">
                <span>{statusOperacao.tipo || (automaticoAtivo ? "AUTOMATICO" : "PROCESSAMENTO")}</span>
                <strong>{statusOperacao.mensagem}</strong>
                <p>{statusOperacao.detalhe}</p>
              </div>

              <div className="sf-processing-meta">
                <div>
                  <span>Tempo</span>
                  <strong>{fmtTempoDecorrido(statusOperacao.iniciadoEm)}</strong>
                </div>
                <div>
                  <span>Últ. monitoramento</span>
                  <strong>{fmtDate(ultimaAtualizacaoMonitor || statusOperacao.atualizadoEm)}</strong>
                </div>
                <div>
                  <span>Última execução</span>
                  <strong>{ultimaExecucao?.ID_EXECUCAO ? `#${ultimaExecucao.ID_EXECUCAO}` : "-"}</strong>
                  <small>{safeText(ultimaExecucao?.DM_STATUS, "Aguardando")}</small>
                </div>
              </div>
            </section>
          ) : null}



          <section className="sf-explain-card">
            <div className="sf-explain-main">
              <span className="sf-eyebrow">Funcionamento da esteira</span>
              <h2>Automático fica no backend</h2>
              <p>
                Quando a esteira está ligada, o servidor continua executando ciclos mesmo se esta tela for fechada. A tela apenas liga, para e monitora o que está acontecendo.
              </p>
            </div>

            <div className="sf-explain-steps">
              <div>
                <strong>1</strong>
                <span>Escolha a licença</span>
                <small>Use a busca para localizar o cliente.</small>
              </div>
              <div>
                <strong>2</strong>
                <span>Sincronize produtos</span>
                <small>Carrega a fila da licença.</small>
              </div>
              <div>
                <strong>3</strong>
                <span>Ligue a esteira</span>
                <small>O backend processa por intervalo.</small>
              </div>
              <div>
                <strong>4</strong>
                <span>Monitore resultados</span>
                <small>Veja consumo, execuções e pendências.</small>
              </div>
            </div>

            <div className={`sf-server-status ${automaticoBackendAtivo ? "is-on" : "is-off"}`}>
              <span>Status da licença selecionada</span>
              <strong>{automaticoBackendAtivo ? "Ligado" : "Desligado"}</strong>
              <small>
                {textoStatusAutomatico}
                {automaticoExecutandoAgora ? " • executando agora" : ""}
              </small>
            </div>
          </section>

          {obsAlertas.length ? (
            <section className="sf-observability-alerts">
              {obsAlertas.map((alerta, index) => (
                <article
                  key={`${alerta?.tipo || "ALERTA"}-${index}`}
                  className={`sf-observability-alert sf-observability-alert--${String(alerta?.severidade || "ATENCAO").toLowerCase()}`}
                >
                  <FiAlertTriangle />
                  <div>
                    <span>{safeText(alerta?.tipo, "ALERTA OPERACIONAL")}</span>
                    <strong>{safeText(alerta?.mensagem)}</strong>
                  </div>
                </article>
              ))}
            </section>
          ) : null}

          {loading ? <div className="sf-progress"><div /></div> : null}

          <section className="sf-client-card">
            <div className="sf-section-title">
              <div className="sf-section-icon"><FiUser /></div>
              <div>
                <h2>Cliente selecionado</h2>
                <p>Dados carregados pela rota de clientes usando o número da licença.</p>
              </div>
            </div>

            <div className="sf-client-grid">
              <InfoItem label="Código" value={cliente?.CodigoCad || clienteResumo?.codigoCad || nrLicenca} strong />
              <InfoItem label="Razão social" value={cliente?.RazaoSocial || empresaPrincipal?.razao_social} strong />
              <InfoItem label="Nome fantasia" value={cliente?.NomeFant || empresaPrincipal?.nome_fantasia} />
              <InfoItem label="CNPJ" value={empresaPrincipal?.cnpj || cliente?.CpfCnpjLimpo || cliente?.CpfCnpj} />
              <InfoItem label="Empresa ERP" value={empresaPrincipal?.id ? `#${empresaPrincipal.id}` : "-"} />
              <InfoItem label="CRT / UF" value={`CRT ${empresaPrincipal?.crt || "-"} • ${empresaPrincipal?.uf || cliente?.UF || "-"}`} />
              <InfoItem label="Cidade" value={empresaPrincipal?.cidade || cliente?.Cidade} />
              <InfoItem label="E-mail" value={empresaPrincipal?.email || cliente?.Email} />
            </div>
          </section>

          <section className="sf-manager-overview">
            <div className="sf-manager-overview__head">
              <div>
                <span className="sf-manager-eyebrow">VISÃO DO GESTOR • HOJE</span>
                <h2>Resumo executivo da esteira fiscal</h2>
                <p>
                  O limite diário controla somente chamadas online à IMendes. Hub Fiscal e cache continuam processando sem consumir quota.
                </p>
              </div>
              <span
                className={`sf-manager-status ${
                  errosHoje > 0
                    ? "is-danger"
                    : quotaOnlineEsgotada
                      ? "is-info"
                      : "is-success"
                }`}
              >
                {statusGestor}
              </span>
            </div>

            <div className="sf-manager-kpis">
              <KpiCard
                label="Processados hoje"
                value={fmtInt(obsGestao?.processadosHoje ?? obsProcessamento?.processamentos)}
                hint={`${fmtInt(obsGestao?.produtosDistintosHoje ?? obsProcessamento?.produtosDistintos)} produto(s) distinto(s)`}
                tone="info"
                icon={FiPackage}
              />
              <KpiCard
                label="Resolvidos pelo HUB"
                value={fmtInt(hubHoje)}
                hint="Zero consumo de quota IMendes"
                tone="success"
                icon={FiDatabase}
              />
              <KpiCard
                label="Online IMendes"
                value={fmtInt(consumoHoje)}
                hint={`${percentualConsumo}% de ${fmtInt(limiteHoje)} disponível no dia`}
                tone={quotaOnlineEsgotada ? "warning" : "info"}
                icon={FiZap}
              />
              <KpiCard
                label="Consultas evitadas"
                value={fmtInt(consultasEvitadasHoje)}
                hint={`${fmtInt(hubHoje)} Hub + ${fmtInt(cacheHoje)} cache • ${fmtPercent(taxaEconomiaConsultasHoje)} de economia`}
                tone="success"
                icon={FiShield}
              />
              <KpiCard
                label="Saldo online"
                value={fmtInt(saldoHoje)}
                hint={quotaOnlineEsgotada ? "Online esgotado; Hub/cache continuam" : "Chamadas online ainda disponíveis"}
                tone={quotaOnlineEsgotada ? "warning" : "success"}
                icon={FiCheckCircle}
              />
              <KpiCard
                label="Erros hoje"
                value={fmtInt(errosHoje)}
                hint={`${fmtPercent(obsProcessamento?.taxaReprocessamento)} de reprocessamento`}
                tone={errosHoje > 0 ? "danger" : "success"}
                icon={FiAlertTriangle}
              />
            </div>

            <div className="sf-manager-quota">
              <div className="sf-manager-quota__line">
                <div>
                  <span>Uso da quota IMendes online</span>
                  <strong>{fmtInt(consumoHoje)} / {fmtInt(limiteHoje)}</strong>
                </div>
                <strong>{percentualConsumo}%</strong>
              </div>
              <div className="sf-manager-quota__bar" aria-label={`Consumo da quota IMendes: ${percentualConsumo}%`}>
                <i style={{ width: `${percentualConsumo}%` }} />
              </div>
              <div className={`sf-manager-rule ${quotaOnlineEsgotada ? "is-info" : "is-success"}`}>
                <FiShield />
                <div>
                  <strong>
                    {quotaOnlineEsgotada
                      ? "Quota online atingida — a esteira NÃO para"
                      : "A quota online não limita o processamento pelo Hub"}
                  </strong>
                  <span>
                    {quotaOnlineEsgotada
                      ? "Novas chamadas HTTP à IMendes ficam aguardando; produtos com regra no Hub ou cache continuam sendo resolvidos."
                      : "Ao chegar no limite diário, somente os produtos que realmente precisarem de uma nova chamada à IMendes serão adiados."}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <nav className="sf-tabs">
            <button className={aba === "observabilidade" ? "active" : ""} onClick={() => setAba("observabilidade")} type="button"><FiShield /> Observabilidade</button>
            <button className={aba === "processamento" ? "active" : ""} onClick={() => setAba("processamento")} type="button"><FiActivity /> Processamento</button>
            <button className={aba === "pendencias" ? "active" : ""} onClick={() => setAba("pendencias")} type="button"><FiAlertTriangle /> Pendências</button>
            <button className={aba === "produtos" ? "active" : ""} onClick={() => setAba("produtos")} type="button"><FiPackage /> Produtos</button>
            <button className={aba === "execucoes" ? "active" : ""} onClick={() => setAba("execucoes")} type="button"><FiList /> Execuções</button>
          </nav>


          {aba === "observabilidade" ? (
            <>
              <section className="sf-observability-kpis">
                <KpiCard
                  label="Tentativas IMendes"
                  value={fmtInt(obsImendes?.tentativasNoPeriodo)}
                  hint="Tentativas registradas no período"
                  tone="info"
                  icon={FiActivity}
                />
                <KpiCard
                  label="Consultas online"
                  value={fmtInt(obsImendes?.consultasOnlineNoPeriodo)}
                  hint="Chamadas que efetivamente consumiram canal online"
                  tone="warning"
                  icon={FiZap}
                />
                <KpiCard
                  label="Resolvidos pelo HUB"
                  value={fmtInt(hubHoje)}
                  hint="Regra reaproveitada por EAN + CRT"
                  tone="success"
                  icon={FiDatabase}
                />
                <KpiCard
                  label="Consultas evitadas"
                  value={fmtInt(consultasEvitadasHoje)}
                  hint={`${fmtPercent(taxaEconomiaConsultasHoje)} de economia • Hub + cache`}
                  tone="success"
                  icon={FiShield}
                />
                <KpiCard
                  label="Respostas por cache"
                  value={fmtInt(obsImendes?.cacheNoPeriodo)}
                  hint="Economia de chamadas externas"
                  tone="success"
                  icon={FiDatabase}
                />
                <KpiCard
                  label="Falhas pré-IMendes"
                  value={fmtInt(obsImendes?.falhasPreImendesNoPeriodo)}
                  hint="Erro antes de chamar a API externa"
                  tone={Number(obsImendes?.falhasPreImendesNoPeriodo || 0) > 0 ? "danger" : "success"}
                  icon={FiAlertTriangle}
                />
                <KpiCard
                  label="Falhas IMendes"
                  value={fmtInt(obsImendes?.falhasImendesNoPeriodo)}
                  hint="Falha após tentativa de integração"
                  tone={Number(obsImendes?.falhasImendesNoPeriodo || 0) > 0 ? "danger" : "success"}
                  icon={FiAlertTriangle}
                />
                <KpiCard
                  label="Saneados"
                  value={fmtInt(obsProcessamento?.saneados)}
                  hint="Regra final aplicada por online, cache ou Hub"
                  tone="success"
                  icon={FiCheckCircle}
                />
                <KpiCard
                  label="Reprocessamentos"
                  value={fmtInt(obsProcessamento?.reprocessamentos)}
                  hint={fmtPercent(obsProcessamento?.taxaReprocessamento)}
                  tone={Number(obsProcessamento?.taxaReprocessamento || 0) >= 50 ? "danger" : "warning"}
                  icon={FiRefreshCw}
                />
                <KpiCard
                  label="Em backoff"
                  value={fmtInt(obsFila?.emBackoff)}
                  hint={`Retry padrão: ${fmtInt(obsEsteira?.retryErroMinutos)} min`}
                  tone={Number(obsFila?.emBackoff || 0) > 0 ? "warning" : "success"}
                  icon={FiClock}
                />
              </section>

              <section className="sf-observability-grid">
                <div className="sf-panel">
                  <div className="sf-section-headline">
                    <div className="sf-section-title">
                      <div className="sf-section-icon"><FiActivity /></div>
                      <div>
                        <h2>Resumo operacional de hoje</h2>
                        <p>Indicadores consolidados da licença sem necessidade de consulta direta ao banco.</p>
                      </div>
                    </div>
                    <button
                      className="sf-btn sf-btn--soft"
                      type="button"
                      onClick={() => carregarObservabilidade()}
                    >
                      <FiRefreshCw /> Atualizar
                    </button>
                  </div>

                  <div className="sf-metric-list">
                    <div><span>Execuções</span><strong>{fmtInt(obsProcessamento?.execucoes)}</strong></div>
                    <div><span>Processamentos</span><strong>{fmtInt(obsProcessamento?.processamentos)}</strong></div>
                    <div><span>Produtos distintos</span><strong>{fmtInt(obsProcessamento?.produtosDistintos)}</strong></div>
                    <div><span>Reprocessamentos</span><strong>{fmtInt(obsProcessamento?.reprocessamentos)}</strong></div>
                    <div><span>Taxa de reprocessamento</span><strong className={Number(obsProcessamento?.taxaReprocessamento || 0) >= 80 ? "is-danger" : ""}>{fmtPercent(obsProcessamento?.taxaReprocessamento)}</strong></div>
                    <div><span>Regras saneadas</span><strong>{fmtInt(obsProcessamento?.saneados)}</strong></div>
                    <div><span>Resolvidos pelo Hub</span><strong>{fmtInt(hubHoje)}</strong></div>
                    <div><span>Resolvidos por cache</span><strong>{fmtInt(cacheHoje)}</strong></div>
                    <div><span>Consultas online evitadas</span><strong>{fmtInt(consultasEvitadasHoje)}</strong></div>
                    <div><span>Economia de consultas</span><strong>{fmtPercent(taxaEconomiaConsultasHoje)}</strong></div>
                    <div><span>Validados operacionalmente</span><strong>{fmtInt(obsProcessamento?.operacionalmenteValidados)}</strong></div>
                    <div><span>Erros</span><strong className={Number(obsProcessamento?.erros || 0) > 0 ? "is-danger" : ""}>{fmtInt(obsProcessamento?.erros)}</strong></div>
                    <div><span>Primeiro processamento</span><strong>{fmtDate(obsProcessamento?.primeiroProcessamento)}</strong></div>
                    <div><span>Último processamento</span><strong>{fmtDate(obsProcessamento?.ultimoProcessamento)}</strong></div>
                  </div>
                </div>

                <div className="sf-panel">
                  <div className="sf-section-title">
                    <div className="sf-section-icon"><FiList /></div>
                    <div>
                      <h2>Fila atual</h2>
                      <p>Somente o último histórico de cada produto entra no estado atual da fila.</p>
                    </div>
                  </div>

                  <div className="sf-queue-grid">
                    {Object.entries(obsFila?.porStatus || {}).length ? (
                      Object.entries(obsFila?.porStatus || {}).map(([status, total]) => (
                        <div key={status} className="sf-queue-item">
                          <StatusBadge status={status} />
                          <strong>{fmtInt(total)}</strong>
                        </div>
                      ))
                    ) : (
                      <div className="sf-empty">
                        <FiList />
                        <strong>Fila sem dados</strong>
                        <small>Atualize a observabilidade para carregar os status.</small>
                      </div>
                    )}
                  </div>

                  <div className="sf-queue-summary">
                    <div><span>Total atual</span><strong>{fmtInt(obsFila?.totalProdutosAtuais)}</strong></div>
                    <div><span>Em backoff</span><strong>{fmtInt(obsFila?.emBackoff)}</strong></div>
                    <div><span>Status esteira</span><strong>{safeText(obsEsteira?.status)}</strong></div>
                    <div><span>Timer</span><strong>{obsEsteira?.timerAtivo ? "Ativo" : "Inativo"}</strong></div>
                  </div>
                </div>
              </section>

              {observabilidade?.telemetria?.observacao ? (
                <div className="sf-telemetry-note">
                  <FiDatabase />
                  <div>
                    <strong>Telemetria detalhada</strong>
                    <span>{observabilidade.telemetria.observacao}</span>
                    <small>
                      Dados detalhados desde: {fmtDate(observabilidade?.telemetria?.detalhadaDesde)}
                    </small>
                  </div>
                </div>
              ) : null}

              <section className="sf-panel">
                <div className="sf-section-headline">
                  <div className="sf-section-title">
                    <div className="sf-section-icon"><FiClock /></div>
                    <div>
                      <h2>Histórico diário</h2>
                      <p>Últimos 30 dias com volume, eficiência, cache, falhas e reprocessamentos.</p>
                    </div>
                  </div>
                  <button className="sf-btn sf-btn--soft" type="button" onClick={() => carregarObservabilidadeDias()}>
                    <FiRefreshCw /> Atualizar
                  </button>
                </div>

                <div className="sf-table-wrap">
                  <table className="sf-table sf-table--observability">
                    <thead>
                      <tr>
                        <th>Dia</th>
                        <th>Execuções</th>
                        <th>Processamentos</th>
                        <th>Distintos</th>
                        <th>Reprocess.</th>
                        <th>Taxa</th>
                        <th>Online</th>
                        <th>Hub</th>
                        <th>Cache</th>
                        <th>Evitadas</th>
                        <th>Economia</th>
                        <th>Falha pré</th>
                        <th>Falha IMendes</th>
                        <th>Saneados</th>
                        <th>Erros</th>
                      </tr>
                    </thead>
                    <tbody>
                      {observabilidadeDias?.data?.length ? observabilidadeDias.data.map((row) => (
                        <tr key={row.DIA}>
                          <td><strong>{fmtDateOnly(row.DIA)}</strong></td>
                          <td>{fmtInt(row.EXECUCOES)}</td>
                          <td>{fmtInt(row.PROCESSAMENTOS)}</td>
                          <td>{fmtInt(row.PRODUTOS_DISTINTOS)}</td>
                          <td>{fmtInt(row.REPROCESSAMENTOS)}</td>
                          <td><span className={Number(row.TAXA_REPROCESSAMENTO || 0) >= 80 ? "sf-value-danger" : ""}>{fmtPercent(row.TAXA_REPROCESSAMENTO)}</span></td>
                          <td>{fmtInt(row.CONSULTAS_ONLINE)}</td>
                          <td>{fmtInt(row.HUB_FISCAL)}</td>
                          <td>{fmtInt(row.CACHE)}</td>
                          <td>{fmtInt(row.CONSULTAS_EVITADAS)}</td>
                          <td>{fmtPercent(row.TAXA_ECONOMIA_CONSULTAS)}</td>
                          <td>{fmtInt(row.FALHAS_PRE_IMENDES)}</td>
                          <td>{fmtInt(row.FALHAS_IMENDES)}</td>
                          <td>{fmtInt(row.SANEADOS)}</td>
                          <td><span className={Number(row.ERROS || 0) > 0 ? "sf-value-danger" : ""}>{fmtInt(row.ERROS)}</span></td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="15">
                            <div className="sf-empty">
                              <FiClock />
                              <strong>Sem histórico diário</strong>
                              <small>Ainda não há dados para o período consultado.</small>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="sf-panel">
                <div className="sf-section-headline">
                  <div className="sf-section-title">
                    <div className="sf-section-icon"><FiAlertTriangle /></div>
                    <div>
                      <h2>Erros e reprocessamentos</h2>
                      <p>Identifique loops, falhas pré-IMendes e produtos que estão consumindo tentativas repetidas.</p>
                    </div>
                  </div>

                  <div className="sf-inline-filters">
                    <label className="sf-filter-check">
                      <input
                        type="checkbox"
                        checked={somenteReprocessados}
                        onChange={(e) => setSomenteReprocessados(e.target.checked)}
                      />
                      <span>Somente reprocessados</span>
                    </label>
                    <div className="sf-search">
                      <FiSearch />
                      <input
                        value={buscaErroObservabilidade}
                        onChange={(e) => setBuscaErroObservabilidade(e.target.value)}
                        placeholder="Produto, NCM ou erro"
                        onKeyDown={(e) => e.key === "Enter" && carregarObservabilidadeErros()}
                      />
                    </div>
                    <button className="sf-btn sf-btn--soft" type="button" onClick={() => carregarObservabilidadeErros()}>
                      <FiRefreshCw /> Buscar
                    </button>
                  </div>
                </div>

                <div className="sf-table-wrap">
                  <table className="sf-table sf-table--observability-errors">
                    <thead>
                      <tr>
                        <th>Produto</th>
                        <th>Tentativas</th>
                        <th>Erros</th>
                        <th>Online / Cache</th>
                        <th>Última origem</th>
                        <th>Status atual</th>
                        <th>Último motivo</th>
                        <th>Backoff</th>
                        <th>Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {observabilidadeErros?.data?.length ? observabilidadeErros.data.map((row) => (
                        <tr key={row.ID_PRODUTO_SERVICO}>
                          <td>
                            <strong>{safeText(row.DS_PRODUTO_SERVICO, `Produto #${row.ID_PRODUTO_SERVICO}`)}</strong>
                            <small>ID {safeText(row.ID_PRODUTO_SERVICO)} • NCM {safeText(row.CD_NCM_ORIGINAL)}</small>
                          </td>
                          <td><strong>{fmtInt(row.VEZES_PROCESSADO)}</strong></td>
                          <td><span className={Number(row.QTDE_ERROS || 0) > 0 ? "sf-value-danger" : ""}>{fmtInt(row.QTDE_ERROS)}</span></td>
                          <td>
                            <strong>{fmtInt(row.CONSULTAS_ONLINE)} online</strong>
                            <small>{fmtInt(row.CACHE)} cache</small>
                          </td>
                          <td>
                            <span className="sf-origin-pill">{safeText(row.ULTIMA_ORIGEM, "LEGADO/SEM ORIGEM")}</span>
                            <small>HTTP {safeText(row.ULTIMO_HTTP_STATUS_IMENDES)}</small>
                          </td>
                          <td><StatusBadge status={row.STATUS_ATUAL || row.ULTIMO_STATUS_DEPOIS} /></td>
                          <td>
                            <strong className="sf-wrap-text">{safeText(row.ULTIMO_MOTIVO || row.MOTIVO_ATUAL)}</strong>
                            <small>{fmtDate(row.ULTIMA_TENTATIVA)}</small>
                          </td>
                          <td>
                            {row.DATA_PROXIMA_CONSULTA_PERMITIDA ? (
                              <span className="sf-backoff-badge">
                                Até {fmtDate(row.DATA_PROXIMA_CONSULTA_PERMITIDA)}
                              </span>
                            ) : "-"}
                          </td>
                          <td>
                            <button
                              className="sf-line-btn"
                              type="button"
                              onClick={() => abrirHistoricoProduto(row.ID_PRODUTO_SERVICO)}
                            >
                              <FiFileText /> Histórico
                            </button>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="9">
                            <div className="sf-empty">
                              <FiCheckCircle />
                              <strong>Nenhum erro/reprocessamento encontrado</strong>
                              <small>Os filtros atuais não retornaram produtos problemáticos.</small>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="sf-result-summary">
                  <span>{fmtInt(observabilidadeErros?.total)} produto(s) encontrado(s)</span>
                  <strong>Exibindo até {fmtInt(observabilidadeErros?.limit || 50)} por consulta</strong>
                </div>
              </section>
            </>
          ) : null}

          {aba === "processamento" ? (
            <section className="sf-layout-two">
              <div className="sf-panel">
                <div className="sf-section-title">
                  <div className="sf-section-icon"><FiClock /></div>
                  <div>
                    <h2>Acompanhamento em tempo real</h2>
                    <p>Log operacional da tela. Cada rodada grava também execução no backend.</p>
                  </div>
                </div>

                <div className="sf-log-list">
                  {logs.length ? logs.map((log) => (
                    <article key={log.id} className={`sf-log sf-log--${log.tipo}`}>
                      <span>{fmtDate(log.data)}</span>
                      <strong>{log.mensagem}</strong>
                      {log.payload ? <pre>{JSON.stringify(log.payload, null, 2)}</pre> : null}
                    </article>
                  )) : (
                    <div className="sf-empty"><FiFileText /><strong>Nenhum evento ainda</strong><small>Inicie uma sincronização ou processamento para acompanhar.</small></div>
                  )}
                </div>
              </div>

              <aside className="sf-panel">
                <div className="sf-section-title">
                  <div className="sf-section-icon"><FiZap /></div>
                  <div>
                    <h2>Último retorno</h2>
                    <p>Resultado resumido da última chamada do lote diário.</p>
                  </div>
                </div>

                {ultimoResultado ? (
                  <pre className="sf-json-box">{JSON.stringify(ultimoResultado, null, 2)}</pre>
                ) : operacaoVisualAtiva ? (
                  <div className="sf-empty">
                    <FiActivity />
                    <strong>Processamento em andamento</strong>
                    <small>Aguardando o primeiro retorno técnico da API.</small>
                  </div>
                ) : (
                  <div className="sf-empty"><FiActivity /><strong>Sem processamento</strong><small>Rode uma rodada para ver o retorno técnico.</small></div>
                )}
              </aside>
            </section>
          ) : null}

          {aba === "pendencias" ? (
            <section className="sf-panel">
              <div className="sf-section-headline">
                <div className="sf-section-title">
                  <div className="sf-section-icon"><FiAlertTriangle /></div>
                  <div>
                    <h2>Pendências IMendes</h2>
                    <p>Lista baseada sempre no último histórico incluído por produto.</p>
                  </div>
                </div>

                <div className="sf-inline-filters">
                  <select value={statusPendencia} onChange={(e) => setStatusPendencia(e.target.value)}>
                    {STATUS_PENDENCIA.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                  <button className="sf-btn sf-btn--soft" type="button" onClick={() => carregarPendencias()}><FiRefreshCw /> Atualizar</button>
                </div>
              </div>

              <div className="sf-table-wrap">
                <table className="sf-table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Cliente</th>
                      <th>NCM</th>
                      <th>Status</th>
                      <th>Última consulta</th>
                      <th>Motivo</th>
                      <th>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendencias.data.length ? pendencias.data.map((row) => (
                      <tr key={row.ID_PRODUTO_REGRA_SANEADA}>
                        <td><strong>{safeText(row.DS_PRODUTO_SERVICO)}</strong><small>Cód. {safeText(row.CD_PRODUTO_SERVICO)} • EAN {safeText(row.CD_EAN)}</small></td>
                        <td><strong>{safeText(row.NR_LICENCA)}</strong><small>{safeText(row.NOME_FANTASIA || row.NM_FANTASIA || row.RAZAO_SOCIAL || cliente?.RazaoSocial)}</small></td>
                        <td>{safeText(row.CD_NCM_ORIGINAL || row.CD_NCM_IMENDES)}</td>
                        <td><StatusBadge status={row.DM_STATUS} /></td>
                        <td>{fmtDate(row.DATA_ULTIMA_CONSULTA_IMENDES || row.DATA_ATUALIZACAO)}</td>
                        <td>{safeText(row.MOTIVO_STATUS)}</td>
                        <td><button className="sf-line-btn" type="button" onClick={() => abrirManual(row)}><FiEdit3 /> Sanear</button></td>
                      </tr>
                    )) : <tr><td colSpan="7"><div className="sf-empty"><FiCheckCircle /><strong>Nenhuma pendência encontrada</strong><small>Altere os filtros ou processe novos produtos.</small></div></td></tr>}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {aba === "produtos" ? (
            <section className="sf-panel">
              <div className="sf-section-headline">
                <div className="sf-section-title">
                  <div className="sf-section-icon"><FiPackage /></div>
                  <div>
                    <h2>Produtos da licença</h2>
                    <p>Controle de saneamento/histórico por produto.</p>
                  </div>
                </div>

                <div className="sf-inline-filters">
                  <div className="sf-search"><FiSearch /><input value={searchProduto} onChange={(e) => setSearchProduto(e.target.value)} placeholder="Produto, código ou NCM" onKeyDown={(e) => e.key === "Enter" && carregarProdutos()} /></div>
                  <select value={statusProduto} onChange={(e) => setStatusProduto(e.target.value)}>
                    {STATUS_PRODUTOS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                  <button className="sf-btn sf-btn--soft" type="button" onClick={() => carregarProdutos()}><FiRefreshCw /> Buscar</button>
                </div>
              </div>

              <div className="sf-table-wrap">
                <table className="sf-table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>NCM</th>
                      <th>Regra IMendes/manual</th>
                      <th>Status</th>
                      <th>Atualização</th>
                      <th>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {produtos.data.length ? produtos.data.map((row) => (
                      <tr key={row.ID_PRODUTO_REGRA_SANEADA}>
                        <td><strong>{safeText(row.DS_PRODUTO_SERVICO)}</strong><small>ID {safeText(row.ID_PRODUTO_SERVICO)} • Cód. {safeText(row.CD_PRODUTO_SERVICO)}</small></td>
                        <td><strong>{safeText(row.CD_NCM_ORIGINAL)}</strong><small>CEST {safeText(row.CD_CEST_ORIGINAL)}</small></td>
                        <td><small>CFOP {safeText(row.CFOP_IMENDES || row.CFOP_ORIGINAL)} • CST {safeText(row.CST_ICMS_IMENDES || row.CSOSN_IMENDES || row.CST_ICMS_ORIGINAL || row.CSOSN_ORIGINAL)}</small></td>
                        <td><StatusBadge status={row.DM_STATUS} /></td>
                        <td>{fmtDate(row.DATA_ATUALIZACAO || row.DATA_CADASTRO)}</td>
                        <td>
                          <button className="sf-line-btn" type="button" onClick={() => abrirHistoricoProduto(row.ID_PRODUTO_SERVICO)}>
                            <FiFileText /> Histórico
                          </button>
                        </td>
                      </tr>
                    )) : <tr><td colSpan="6"><div className="sf-empty"><FiPackage /><strong>Nenhum produto carregado</strong><small>Sincronize os produtos da licença ou ajuste os filtros.</small></div></td></tr>}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {aba === "execucoes" ? (
            <section className="sf-panel">
              <div className="sf-section-headline">
                <div className="sf-section-title">
                  <div className="sf-section-icon"><FiList /></div>
                  <div>
                    <h2>Execuções</h2>
                    <p>Histórico rastreável dos processamentos da licença.</p>
                  </div>
                </div>
                <button className="sf-btn sf-btn--soft" type="button" onClick={() => carregarExecucoes()}><FiRefreshCw /> Atualizar</button>
              </div>

              <div className="sf-table-wrap">
                <table className="sf-table">
                  <thead>
                    <tr>
                      <th>Execução</th>
                      <th>Tipo</th>
                      <th>Status</th>
                      <th>Analisados</th>
                      <th>Consultas</th>
                      <th>Saneados</th>
                      <th>Início/Fim</th>
                    </tr>
                  </thead>
                  <tbody>
                    {execucoes.data.length ? execucoes.data.map((row) => (
                      <tr key={row.ID_EXECUCAO}>
                        <td>#{safeText(row.ID_EXECUCAO)}</td>
                        <td>{safeText(row.TIPO_EXECUCAO)}</td>
                        <td><StatusBadge status={row.DM_STATUS} /></td>
                        <td>{fmtInt(row.QTDE_PRODUTOS_ANALISADOS)}</td>
                        <td>{fmtInt(row.QTDE_CONSULTAS_IMENDES)}</td>
                        <td>{fmtInt(row.QTDE_SANEADOS)}</td>
                        <td><small>{fmtDate(row.DATA_INICIO)}<br />{fmtDate(row.DATA_FIM)}</small></td>
                      </tr>
                    )) : <tr><td colSpan="7"><div className="sf-empty"><FiList /><strong>Nenhuma execução encontrada</strong><small>Rode uma rodada para gerar histórico.</small></div></td></tr>}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}
        </div>

        <ManualModal
          pendencia={modalManual}
          form={formManual}
          setForm={setFormManual}
          salvando={salvandoManual}
          onClose={() => setModalManual(null)}
          onSalvar={salvarManual}
        />

        <HistoricoProdutoModal
          historico={historicoProduto}
          carregando={carregandoHistorico}
          onClose={() => {
            if (!carregandoHistorico) setHistoricoProduto(null);
          }}
        />
      </div>
    </MainLayout>
  );
}
